import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Menu, X, ChevronDown } from 'lucide-react';
import LanguageToggle from './LanguageToggle';
import { translations } from '../utils/translations';

const Header = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleDropdown = (menu: string) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navItems = [
    { 
      key: 'solutions', 
      label: t.nav.solutions, 
      hasDropdown: true,
      href: '/solutions'
    },
    { 
      key: 'services', 
      label: language === 'en' ? 'Services' : 'Servicios', 
      hasDropdown: false, 
      href: '/services'
    },
    { 
      key: 'industries', 
      label: t.nav.industries, 
      hasDropdown: false,
      href: '/industries'
    },
    { 
      key: 'about', 
      label: language === 'en' ? 'About' : 'Nosotros', 
      hasDropdown: false,
      href: '/about'
    },
    { 
      key: 'blog', 
      label: 'Blog', 
      hasDropdown: false,
      href: '/blog'
    },
  ];

  return (
    <>
      {/* Spacer to prevent content jump - increased for sticky header */}
      <div className={`transition-all duration-300 ${isScrolled ? 'h-20' : 'h-24'}`}></div>

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'py-2 bg-white shadow-lg' : 'py-4 bg-transparent'
        }`}
        style={{
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)'
        }}
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <div className={`font-bold transition-all duration-300 ${
                  isScrolled ? 'text-2xl md:text-2xl' : 'text-2xl md:text-3xl'
                }`}>
                  <span style={{ color: '#115d7b' }}>Laim</span>
                  <span style={{ color: '#ff8c00' }}>u</span>
                </div>
                <span className="sr-only">LAIMU Trantor - Software ERP México</span>
              </a>
            </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.key} className="relative group">
                <a
                  href={item.href}
                  className="flex items-center text-gray-700 hover:text-teal-600 font-medium transition-all duration-300 py-2 px-3 rounded-lg hover:bg-gray-50/80"
                >
                  {item.label}
                  {item.hasDropdown && (
                    <ChevronDown className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                  )}
                </a>
                
                {item.hasDropdown && (
                  <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-xl bg-white/95 backdrop-blur-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top scale-95 group-hover:scale-100">
                    <div className="py-1">
                      {Object.entries(t.solutions.modules).map(([key, value]) => (
                        <a 
                          key={key}
                          href={`/module/${key}`} 
                          className="block px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-teal-600 transition-colors duration-200 first:rounded-t-xl last:rounded-b-xl"
                        >
                          {value.title}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

            <div className="hidden lg:flex items-center space-x-4">
              <LanguageToggle />
              <a
                href="/success-cases"
                className="px-4 py-2 border rounded-xl hover:bg-gray-50 transition-all duration-300 font-medium hover:shadow-md"
                style={{ color: '#115d7b', borderColor: '#115d7b' }}
              >
                {language === 'en' ? 'Success Cases' : 'Casos de Éxito'}
              </a>
              <a
                href="/demo"
                className={`px-6 py-3 text-white rounded-xl transition-all duration-300 font-bold shadow-lg hover:shadow-2xl transform hover:scale-105 hover:-translate-y-0.5 ${
                  isScrolled ? 'animate-pulse-slow' : ''
                }`}
                style={{
                  backgroundColor: '#ff8c00',
                  boxShadow: '0 4px 14px rgba(255, 140, 0, 0.4)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e67e00';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 140, 0, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ff8c00';
                  e.currentTarget.style.boxShadow = '0 4px 14px rgba(255, 140, 0, 0.4)';
                }}
              >
                {language === 'en' ? '🚀 Request Demo' : '🚀 Solicitar Demo'}
              </a>
            </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <LanguageToggle />
            <button
              onClick={toggleMobileMenu}
              className="ml-4 p-2 text-gray-700 hover:text-teal-600 focus:outline-none rounded-lg hover:bg-gray-50 transition-all duration-300"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed top-24 left-4 right-4 z-40 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-100 mt-2">
          <div className="container mx-auto px-4 py-4">
            <nav className="flex flex-col space-y-4">
              {navItems.map((item) => (
                <div key={item.key}>
                  <a
                    href={item.href}
                    onClick={toggleMobileMenu}
                    className="flex items-center justify-between w-full text-gray-700 hover:text-teal-600 font-medium transition-colors py-2 px-3 rounded-lg hover:bg-gray-50"
                  >
                    <span>{item.label}</span>
                  </a>
                </div>
              ))}
            </nav>
            <div className="flex flex-col space-y-2 mt-6">
              <a
                href="/success-cases"
                className="px-4 py-2 text-center border rounded-xl hover:bg-gray-50 transition-colors font-medium"
                style={{ color: '#115d7b', borderColor: '#115d7b' }}
                onClick={toggleMobileMenu}
              >
                {language === 'en' ? 'Success Cases' : 'Casos de Éxito'}
              </a>
              <a
                href="/demo"
                className="px-4 py-3 text-center text-white rounded-xl transition-all duration-300 font-bold shadow-lg"
                style={{
                  backgroundColor: '#ff8c00',
                  boxShadow: '0 4px 14px rgba(255, 140, 0, 0.4)'
                }}
                onClick={toggleMobileMenu}
              >
                {language === 'en' ? '🚀 Request Demo' : '🚀 Solicitar Demo'}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;